import { Fragment, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { ModuleRegistry } from "ag-grid-community";
import { AllCommunityModule, ClientSideRowModelModule, CsvExportModule } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Toast } from "../../../../config/Toast";
import { FormatSalaryItem } from "../../../../helper/ResponseSalary";
import { ColumnSalaryCalculation } from "./ColumnSalaryCalculation";
import "flatpickr/dist/themes/light.css";
import moment from "moment";

import Pagination from "../../../../components/Pagination";
import SalaryCalculationControls from "./SalaryCalculationControls";

// Services
import reportService from "../../../../services/Report";

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule, CsvExportModule]);

const notify = {
  success: (msg) => toast.success(msg, Toast),
  error: (msg) => toast.error(msg, Toast),
  info: (msg) => toast.info(msg, Toast),
};

const limitDataPages = 10;

const SalaryCalculation = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rangeDate, setRangeDate] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [search, setSearch] = useState(null);
  const [totalPage, setTotalPage] = useState(0);

  const [payloadSort, setPayloadSort] = useState({ sort_by: null, sort_type: null });

  const fetchSalaryCalculation = useCallback(
    async (opts = {}) => {
      if (!rangeDate[0] || !rangeDate[1]) return;

      setLoading(true);

      try {
        const params = {
          page: opts.page || pageNumber,
          limit: opts.limit || limitDataPages,
          q: opts.search ?? search,
          start: moment(rangeDate[0]).format("YYYY-MM-DD"),
          end: moment(rangeDate[1]).format("YYYY-MM-DD"),
          sort_by: opts.sort_by || payloadSort?.sort_by,
          sort_type: opts.sort_type || payloadSort?.sort_type,
        };

        const { data, total_page = 0 } =
          (
            await reportService.salaryCalculation(
              params.page,
              params.limit,
              params.q,
              params.start,
              params.end,
              params.sort_by,
              params.sort_type,
              params.source_project,
              params.sesi
            )
          )?.data || {};

        setRowData(FormatSalaryItem(data || [], params.page, params.limit));
        setTotalPage(total_page);
      } catch (err) {
        notify.error(err?.response?.data?.message || "Gagal memuat data Salary Calculation");
      } finally {
        setTimeout(() => setLoading(false), 400);
      }
    },
    [rangeDate, pageNumber, search, payloadSort]
  );

  useEffect(() => {
    if (rangeDate[0] && rangeDate[1]) fetchSalaryCalculation({ page: pageNumber });
  }, [rangeDate, pageNumber, search, payloadSort, fetchSalaryCalculation]);

  const handleSearch = useCallback((val) => {
    setPageNumber(1);
    setSearch(val);
  }, []);

  const handleDateChange = useCallback((dates) => {
    if (Array.isArray(dates) && dates.length === 2) {
      setRangeDate(dates);
      setPageNumber(1);
    }
  }, []);

  const colDefs = useMemo(
    () =>
      ColumnSalaryCalculation(
        (payload) => setPayloadSort(payload),
      ),
    []
  );

  const handleChangePage = useCallback(({ selected }) => setPageNumber(selected + 1), []);

  return (
    <Fragment>
      <div className="d-flex flex-column justify-content-end align-items-center w-100 h-100 p-0 m-0 gap-4">
        <SalaryCalculationControls
          onDateChange={handleDateChange}
          handleSearch={handleSearch}
        />

        <div className="flex flex-row w-full p-0 m-0 z-[9999]">
          <div className="w-full p-0 m-0">
            <div
              className="ag-theme-alpine w-100 relative"
              style={{
                height: 50 * rowData.length + 50 + "px",
                maxHeight: 50 * rowData.length + 50 + "px",
                minHeight: "250px",
                overflow: "auto",
              }}
            >
              <AgGridReact
                rowData={loading ? [] : rowData}
                columnDefs={colDefs}
                domLayout="normal"
                theme="legacy"
                rowHeight={50}
                headerHeight={50}
                animateRows={true}
                enableCellTextSelection={true}
                sideBar={false}
                defaultColDef={{ sortable: true, filter: false, resizable: true }}
                overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Memuat data...</span>'}
                overlayNoRowsTemplate={
                  '<span style="display: none !important;" class="ag-overlay-loading-center">Tidak ada data</span>'
                }
              />
              {rowData.length === 0 && (
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
                  <span className="ag-overlay-loading-center">Tidak ada data</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {totalPage > 0 && <Pagination pageCount={totalPage} forcePage={pageNumber - 1} changePage={handleChangePage} />}
      </div>
    </Fragment>
  );
};

export default SalaryCalculation;
